import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * KIR-5098: the endpoint(s) that matched the active endpoint filter, shown on
 * the closed row so the user need not expand it. Renders nothing when no
 * endpoint filter is applied (the API then omits the fields).
 */
export function MatchedEndpointChip({
  matchedEndpoints,
  matchedEndpointCount,
}: {
  matchedEndpoints?: string[];
  matchedEndpointCount?: number;
}): ReactElement | null {
  const first = matchedEndpoints?.[0];
  if (!first) return null;
  // A shared machine can carry two hostnames (one per user), so never show
  // fewer than the labels we have.
  const total = Math.max(matchedEndpointCount ?? 0, matchedEndpoints.length);
  const more = total - 1;

  const chip = (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] leading-4 text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
      data-testid="matched-endpoint-chip"
    >
      <span className="shrink-0 opacity-75">endpoint</span>
      <span className="truncate font-mono font-medium">{first}</span>
      {more > 0 && <span className="shrink-0 opacity-75">+{more}</span>}
    </span>
  );

  if (more <= 0) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5 font-mono text-xs">
          {matchedEndpoints.map(label => (
            <span key={label}>{label}</span>
          ))}
          {total > matchedEndpoints.length && (
            <span className="font-sans opacity-75">
              and {total - matchedEndpoints.length} more
            </span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/** Endpoints count on a grouped row. */
export function EndpointCountCell({ count }: { count?: number }): ReactElement {
  return (
    <span className="tabular-nums">{count === undefined ? '—' : count}</span>
  );
}
